import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Printer, BadgeCheck, FlaskConical } from "lucide-react";

export const CoaModal = ({ coa, open, onClose }) => {
  if (!coa) return null;
  const rows = [
    ["Product", coa.product],
    ["SKU", coa.sku],
    ["Batch / Lot No.", coa.batch],
    ["Net content", coa.net_content],
    ["Appearance", coa.appearance],
    ["Purity (HPLC)", coa.purity],
    ["Test method", coa.method],
    ["Manufacture date", coa.manufacture_date],
    ["Test date", coa.test_date],
    ["Re-test date", coa.retest_date],
    ["Storage", coa.storage],
    ["Released by", coa.released_by],
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[90] bg-ink-900/90 backdrop-blur-md flex items-center justify-center px-5 py-10 overflow-y-auto"
          data-testid="coa-modal"
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-w-xl w-full bg-ink-800 border border-[#27272A] clip-corner-lg"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#27272A]">
              <div className="flex items-center gap-2.5">
                <FlaskConical size={18} className="text-accent" />
                <div>
                  <p className="font-display font-extrabold text-lg tracking-tight leading-none">
                    CERTIFICATE OF ANALYSIS
                  </p>
                  <p className="font-mono-tech text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-1">
                    Recovery Supps Australia · QC Lab
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="text-zinc-500 hover:text-accent transition-colors" data-testid="coa-close">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
                  Result
                </span>
                <span className="flex items-center gap-1.5 font-display font-bold text-emerald-400 text-lg">
                  <BadgeCheck size={18} /> {coa.result}
                </span>
              </div>
              <div className="divide-y divide-[#27272A] border border-[#27272A]">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex gap-4 px-4 py-2.5">
                    <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 w-40 shrink-0">
                      {k}
                    </span>
                    <span className="text-sm text-zinc-200">{v}</span>
                  </div>
                ))}
              </div>
              <p className="font-mono-tech text-[10px] text-zinc-600 mt-4 leading-relaxed">
                This certificate confirms laboratory testing of the stated batch. Supplied for research
                use only — not for human consumption. This is not a therapeutic good.
              </p>
              <button
                onClick={() => window.print()}
                className="w-full mt-5 flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white font-display font-bold uppercase tracking-wide py-3 transition-colors"
                data-testid="coa-print"
              >
                <Printer size={16} /> Print / Save PDF
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
